/**
 * SubscriptionManagement — Admin page showing trial status, period dates,
 * customer details, and workflow action buttons per row.
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Container, Typography, Box, Chip, Alert, Avatar, TextField, LinearProgress,
  Button, Dialog, DialogTitle, DialogContent, IconButton, Paper
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import CloseIcon from '@mui/icons-material/Close';
import billingApi from '../../services/billingApi';
import { formatDateShort, formatCents, subscriptionStatusColor as statusColor } from '../../utils/formatters';
import WorkflowRunForm from '../../components/workflow/WorkflowRunForm';
import SubscriptionActionsDialog from '../../components/SubscriptionActionsDialog';


export default function SubscriptionManagement() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debounced search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Dialogs
  const [actionsDialogId, setActionsDialogId] = useState(null);
  const [workflowDialog, setWorkflowDialog] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);

      const data = await billingApi.get(`/api/admin/subscriptions?${params}`);
      setRows(data.subscriptions || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search
  function handleSearchChange(value) {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 500);
  }

  async function openWorkflowDialog(workflowId, subscriptionId) {
    setActionsDialogId(null);
    try {
      const data = await billingApi.get(`/api/admin/workflows/definitions/${workflowId}`);
      setWorkflowDialog({ workflowId, subscriptionId, definition: data.definition });
    } catch (err) {
      setError(err.message);
    }
  }

  function handleWorkflowComplete(instance) {
    // 999.4741/999.4758/999.4760 INFO (harrison review): this only closed on
    // completed/failed — manual_review, canceled, and undone all left the
    // dialog open with no terminal feedback and never refreshed the
    // underlying subscriptions list. This dialog doesn't itself claim a
    // success/failure outcome (that's WorkflowRunForm's own admin-mode
    // banner, rendered inside it, which already gives manual_review its own
    // "parked for review" branch) — it just closes and reloads once the
    // workflow has genuinely stopped, so all five terminal statuses belong
    // here, including manual_review.
    if (['completed', 'failed', 'canceled', 'undone', 'manual_review'].includes(instance?.status)) {
      setTimeout(() => {
        setWorkflowDialog(null);
        loadData();
      }, 1500);
    }
  }

  async function handleAdminRetry(subscriptionId) {
    setActionsDialogId(null);
    setRetrying(subscriptionId);
    setError('');
    try {
      await billingApi.post(`/api/admin/subscriptions/${subscriptionId}/retry-payment`);
      setTimeout(loadData, 2000);
    } catch (err) {
      setError(err.data?.message || err.message || 'Payment retry failed');
    } finally {
      setRetrying(null);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'email',
      headerName: 'Customer',
      width: 220,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={row.user_picture}
            sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: '#C8942A' }}
          >
            {(row.user_name || row.email || '?')[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: '0.8rem' }}>
              {row.user_name || row.email}
            </Typography>
            {row.user_name && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {row.email}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'product_name',
      headerName: 'Product',
      width: 130,
    },
    {
      field: 'plan_name',
      headerName: 'Plan',
      width: 130,
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{row.plan_name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {formatCents(row.price_cents)}/{row.interval_type === 'year' ? 'yr' : 'mo'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      type: 'singleSelect',
      filterOptions: ['active', 'trialing', 'past_due', 'paused', 'canceled', 'expired', 'admin_granted', 'pending'],
      renderCell: (row) => (
        <Box>
          <Chip
            label={row.status?.replace(/_/g, ' ')}
            color={statusColor(row.status)}
            size="small" variant="outlined"
            sx={{ fontSize: '0.7rem', fontWeight: 500 }}
          />
          {!!row.cancel_at_period_end && (
            <Typography variant="caption" color="error" sx={{ display: 'block', fontSize: '0.6rem', mt: 0.25 }}>
              Canceling at period end
            </Typography>
          )}
          {row.status === 'past_due' && row.dunning_started_at && (
            <Typography variant="caption" color="error" sx={{ display: 'block', fontSize: '0.6rem', mt: 0.25 }}>
              Attempt {row.dunning_attempt_count || 1} &middot; {Math.max(0, 7 - Math.floor((Date.now() - new Date(row.dunning_started_at).getTime()) / (1000 * 60 * 60 * 24)))}d left
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'started_at',
      headerName: 'Starts',
      width: 120,
      type: 'date',
      renderCell: (row) => {
        if (row.started_at) return <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDateShort(row.started_at)}</Typography>;
        if (row.status === 'pending' && row.current_period_start) {
          return <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic' }}>{formatDateShort(row.current_period_start)}</Typography>;
        }
        return <Typography variant="caption" color="text.secondary">—</Typography>;
      },
    },
    {
      field: 'current_period_end',
      headerName: 'Renews / Ends',
      width: 130,
      type: 'date',
      renderCell: (row) => {
        if (!row.current_period_end) return <Typography variant="caption" color="text.secondary">—</Typography>;
        const periodEnd = new Date(row.current_period_end);
        const isPast = periodEnd < new Date();
        const isSoon = !isPast && (periodEnd - Date.now()) < 7 * 24 * 60 * 60 * 1000;
        return (
          <Typography variant="body2" sx={{
            fontSize: '0.8rem',
            color: isPast ? 'error.main' : isSoon ? 'warning.main' : 'text.primary',
          }}>
            {formatDateShort(row.current_period_end)}
          </Typography>
        );
      },
    },
    {
      field: 'last_payment_at',
      headerName: 'Last Payment',
      width: 150,
      sortable: false,
      type: 'date',
      renderCell: (row) => {
        if (!row.last_payment_at) return <Typography variant="caption" color="text.secondary">—</Typography>;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {formatCents(row.last_payment_amount_cents || 0)}
            </Typography>
            <Chip
              label={row.last_payment_status || '—'}
              color={row.last_payment_status === 'succeeded' ? 'success' : row.last_payment_status === 'failed' ? 'error' : 'default'}
              size="small" variant="outlined"
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
            {row.last_payment_failure_reason && (
              <Typography variant="caption" color="error" sx={{ display: 'block', fontSize: '0.6rem' }}>
                {row.last_payment_failure_reason}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      type: 'date',
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {formatDateShort(row.created_at)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => { e.stopPropagation(); setActionsDialogId(row.id); }}
          sx={{ fontSize: '0.7rem', textTransform: 'none' }}
        >
          Actions
        </Button>
      ),
    },
  ], []);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Subscriptions
        </Typography>
        <Typography variant="body2" color="text.secondary">{total} total</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small" label="Search by email" value={search}
          onChange={e => handleSearchChange(e.target.value)}
          sx={{ minWidth: 250 }}
        />
      </Box>

      <Paper variant="outlined" sx={{ height: 'calc(100vh - 260px)' }}>
        {loading && <LinearProgress />}
        <AdminTable
          rows={rows}
          columns={columns}
          defaultSort={[{ field: 'created_at', direction: 'desc' }]}
          loading={loading}
        />
      </Paper>

      {/* Actions dialog */}
      <SubscriptionActionsDialog
        open={!!actionsDialogId}
        onClose={() => setActionsDialogId(null)}
        subscriptionId={actionsDialogId}
        onRunWorkflow={openWorkflowDialog}
        onRetryPayment={handleAdminRetry}
      />

      {/* Workflow execution dialog */}
      <Dialog open={!!workflowDialog} onClose={() => setWorkflowDialog(null)} maxWidth="sm" fullWidth>
        {workflowDialog && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {workflowDialog.definition?.name || workflowDialog.workflowId?.replace(/_/g, ' ')}
              <IconButton size="small" onClick={() => setWorkflowDialog(null)} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <WorkflowRunForm
                definition={workflowDialog.definition}
                mode="admin"
                prefilledData={{ subscription_id: workflowDialog.subscriptionId }}
                onExecuted={handleWorkflowComplete}
                showProgress
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
}
